/**
 * ScrQuickCopy.tsx — the SCR quick-copy block (Reshape §3.1), pinned to the top
 * of the clinician review.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: to check the patient against the NHS Summary Care Record the clinician
 * re-keys four identifiers into the Spine portal — first name, last name, date
 * of birth and POSTCODE. Re-typing is slow and error-prone, so each field gets a
 * one-tap copy button plus a "Copy all" that assembles the block. The SCR-check
 * CONSENT status sits beside it (the check is only lawful with consent). The
 * NAME comes from the assessment review's Lead-resolved acquisition identity
 * (`name` prop) — the SCR patient record does not capture names yet (v3's MPI
 * captures them at migration); DOB/postcode come from the SCR read. Anything
 * still missing renders "Not recorded".
 *
 * Money never appears here (this is a clinician surface).
 */
import { useState } from 'react';
import type { AssessmentPatientIdentity, ScrBlock } from '../api/types';

/** The Lead-resolved acquisition name that overrides the (name-less) SCR record. */
type PatientName = AssessmentPatientIdentity;
import { formatUtcDate } from '../lib/format';
import { copyToClipboard } from '../staff/clipboard';
import { Alert, Badge, Card, CheckIcon } from '../ui/kit';

/** A DOB may be a date-only string (YYYY-MM-DD) or a full instant — format both. */
function formatDob(dob: string | null): string {
  if (!dob) return '';
  // Date-only strings have no time — formatUtcDate handles ISO instants; for a
  // bare date append midnight UTC so it renders as a UK long date.
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(dob) ? `${dob}T00:00:00.000Z` : dob;
  return formatUtcDate(iso);
}

interface Line {
  key: string;
  label: string;
  /** The raw value to copy (empty string when not recorded). */
  value: string;
  /** What to show on screen (may differ from the copy value, e.g. a long date). */
  display: string;
}

/**
 * The first/last NAME is sourced from the review's Lead-resolved identity
 * (`AssessmentReview.patientIdentity`) when present, falling back to the SCR
 * read — the SCR patient record does not capture names yet, so this is what
 * turns the copy fields from "Not recorded" into the real acquisition name.
 * DOB + postcode stay owned by the SCR read.
 */
function buildLines(scr: ScrBlock, name?: PatientName | null): Line[] {
  const firstName = name?.firstName ?? scr.firstName;
  const lastName = name?.lastName ?? scr.lastName;
  return [
    { key: 'firstName', label: 'First name', value: firstName ?? '', display: firstName ?? 'Not recorded' },
    { key: 'lastName', label: 'Last name', value: lastName ?? '', display: lastName ?? 'Not recorded' },
    { key: 'dob', label: 'Date of birth', value: scr.dateOfBirth ?? '', display: formatDob(scr.dateOfBirth) || 'Not recorded' },
    { key: 'postcode', label: 'Postcode', value: scr.postcode ?? '', display: scr.postcode ?? 'Not recorded' },
  ];
}

/** Small copy button that flips to a "Copied" tick briefly. */
function CopyButton({ label, text, onCopied }: { label: string; text: string; onCopied?: () => void }) {
  const [copied, setCopied] = useState(false);
  const disabled = text.length === 0;
  async function handle() {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      onCopied?.();
      window.setTimeout(() => setCopied(false), 1500);
    }
  }
  return (
    <button
      type="button"
      onClick={handle}
      disabled={disabled}
      aria-label={label}
      style={{
        minWidth: '44px',
        minHeight: '36px',
        padding: '6px 12px',
        borderRadius: 'var(--gps-r-pill)',
        border: '1.5px solid var(--gps-border-strong)',
        background: copied ? 'var(--gps-primary-light)' : 'var(--gps-surface)',
        color: copied ? 'var(--gps-green-text)' : 'var(--gps-text)',
        font: 'inherit',
        fontSize: '13px',
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        flex: 'none',
      }}
    >
      {copied ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          Copied <CheckIcon size={12} />
        </span>
      ) : (
        'Copy'
      )}
    </button>
  );
}

/**
 * The SCR block. `scr` is the identity SCR read; `name` is the Lead-resolved
 * acquisition name from the assessment review (overrides the SCR's name-less
 * record for the first/last copy fields); `consent` is the SCR-check consent
 * status from the assessment (undefined when the review hasn't loaded).
 */
export function ScrQuickCopy({
  scr,
  name,
  consent,
}: {
  scr: ScrBlock;
  name?: PatientName | null;
  consent?: boolean | null;
}) {
  const [status, setStatus] = useState<string | null>(null);
  const lines = buildLines(scr, name);

  async function copyAll() {
    const block = lines
      .filter((l) => l.value)
      .map((l) => `${l.label}: ${l.value}`)
      .join('\n');
    const ok = await copyToClipboard(block);
    setStatus(ok ? 'All SCR details copied to the clipboard.' : 'Sorry — copy is unavailable in this browser.');
    window.setTimeout(() => setStatus(null), 2500);
  }

  const consentBadge =
    consent === true ? (
      <Badge tone="positive">SCR check consented</Badge>
    ) : consent === false ? (
      <Badge tone="pending">SCR check not consented</Badge>
    ) : (
      <Badge tone="muted">Consent status unavailable</Badge>
    );

  return (
    <Card style={{ borderColor: 'var(--gps-border-strong)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-3)', flexWrap: 'wrap', marginBottom: 'var(--gps-s-3)' }}>
        <h2 style={{ fontFamily: 'var(--gps-font-display)', fontSize: '18px', margin: 0, color: 'var(--gps-text)' }}>
          SCR quick-copy
        </h2>
        {consentBadge}
        <button
          type="button"
          onClick={copyAll}
          style={{
            marginLeft: 'auto',
            minHeight: '36px',
            padding: '6px 14px',
            borderRadius: 'var(--gps-r-pill)',
            border: '0',
            background: 'var(--gps-primary)',
            color: 'var(--gps-on-accent)',
            font: 'inherit',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Copy all
        </button>
      </div>

      <p style={{ color: 'var(--gps-muted)', fontSize: '12.5px', margin: '0 0 var(--gps-s-3)' }}>
        Paste these into the Spine SCR check. Confirm consent before viewing the record.
      </p>

      <dl style={{ margin: 0 }}>
        {lines.map((line) => (
          <div
            key={line.key}
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-3)', padding: '8px 0', borderBottom: '1px solid var(--gps-border)' }}
          >
            <dt style={{ color: 'var(--gps-muted)', fontSize: '12.5px', fontWeight: 600, width: '96px', flex: 'none' }}>{line.label}</dt>
            <dd style={{ margin: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 'var(--gps-s-3)' }}>
              <span style={{ flex: 1, color: 'var(--gps-text)', fontSize: '15px', fontWeight: line.value ? 600 : 400 }}>
                {line.value ? line.display : <span style={{ color: 'var(--gps-muted)', fontWeight: 400 }}>{line.display}</span>}
              </span>
              <CopyButton label={`Copy ${line.label.toLowerCase()}`} text={line.value} />
            </dd>
          </div>
        ))}
      </dl>

      {status ? (
        <Alert tone="success" role="status" style={{ marginTop: 'var(--gps-s-3)' }}>
          {status}
        </Alert>
      ) : null}
    </Card>
  );
}
